// Types
import { useCallback } from 'react';
import dayjs from 'dayjs';
import { ELoggedEventItemDataType } from '@timeedit/types/lib/enums/loggedEvent.enum';
import type { TLoggedEvent } from '@timeedit/types/lib/types/loggedEvent.type';
import { EditableTable } from '@timeedit/ui-components';
// Components
import { Typography } from '@timeedit/ui-components/antd';
import TextArea from '@timeedit/ui-components/antd/input/TextArea';
import RenderIcon from './RenderIcon';

/**
 * HELPERS
 */

const parseLogData = (data?: string, dataType?: ELoggedEventItemDataType) => {
  if (!data || !dataType) {
    return '';
  }

  if (dataType === ELoggedEventItemDataType.JSON) {
    // Add indentation and remove " and , from the json string to make it easier to read"
    return JSON.stringify(JSON.parse(data), null, 4);
  }
  if (dataType === ELoggedEventItemDataType.TEXT) {
    return data;
  }

  // just show the data string as is if we dont recognize the type
  return data;
};

const LogItemTable = ({
  record,
  isLoading,
}: {
  record: TLoggedEvent;
  isLoading: boolean;
}) => {
  const columns = [
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: '10%',
      render: (timestamp: number) =>
        dayjs(timestamp * 1000).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: 'Message',
      dataIndex: 'message',
      key: 'message',
      ellipsis: true,
      width: '90%',
    },
  ];

  const footer = useCallback(
    () => (
      <Typography.Paragraph type="secondary">
        id: {record.id}
      </Typography.Paragraph>
    ),
    [record.id]
  );
  return (
    <div className="migration-details-table-wrapper">
      <EditableTable
        loading={isLoading}
        showHeader={false}
        dataSource={record.items.map((item, index) => ({
          ...item,
          id: index,
          key: `${record.id}-${index}`,
        }))}
        columns={columns}
        rowKey={'key'}
        footer={footer}
        expandable={{
          expandedRowRender: (item) => (
            <TextArea
              autoSize
              readOnly
              value={parseLogData(item.data?.content, item.data?.type)}
            />
          ),
          rowExpandable: (log) => !!log.data,
          expandIcon: ({ expanded, onExpand, record }) => {
            return record.data ? (
              <RenderIcon
                expanded={expanded}
                onExpand={onExpand}
                record={record}
              />
            ) : null;
          },
        }}
      />
    </div>
  );
};

export default LogItemTable;
